import { ListShadow } from "@/components/list/list-shadow";
import { useGetPlan } from "@/hooks/requests/plan/use-get-plan";
import { useGetYourFood } from "@/hooks/requests/your-food/use-get-your-food";
import { pendingFoodAddAtom, recentlyAddedMealIdsAtom } from "@/stores/plan-food-add.store";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { IonSkeletonText } from "@ionic/react";
import { useAtomValue } from "jotai";
import { Fragment, memo, useMemo, useState } from "react";
import { getDayMealItems, hoistRecentlyAdded } from "../../utils/plan-week-data";
import { AddingFoodCard } from "../meal-card/adding-food-card";
import { MealCard } from "../meal-card";
import { MyPlanEmptyState } from "../my-plan-empty-state";

interface SingleScrollMealListProps {
  selectedDay: string;
  selectedDate: Date | null;
  selectedDateKey?: string;
  pageRef?: React.RefObject<HTMLElement>;
  /** Opens the NutriCoach chat in general (day-level) mode from the empty state. */
  onAddNext: () => void;
}

/**
 * My Plan v2 "Your Day" list: a single flat, label-less list of the day's meals
 * (breakfast → lunch → dinner → snack) under one "YOUR DAY" header, with meals
 * the coach just added hoisted to the top. While the coach is adding food in
 * the background a temporary "Adding food..." card sits first. An empty day
 * shows the coach empty state instead of per-meal-time placeholder rows.
 */
export const SingleScrollMealList = memo(function SingleScrollMealList({
  selectedDay,
  selectedDateKey,
  onAddNext,
}: SingleScrollMealListProps) {
  const { plan, isLoading: isPlanLoading } = useGetPlan();
  const { yourFoodMealsSet, isLoading: isYourFoodLoading } = useGetYourFood();
  const pendingFoodAdd = useAtomValue(pendingFoodAddAtom);
  const recentlyAddedMealIds = useAtomValue(recentlyAddedMealIdsAtom);
  const [isScrolled, setIsScrolled] = useState(false);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    setIsScrolled(e.currentTarget.scrollTop > 0);
  };

  const items = useMemo(
    () =>
      hoistRecentlyAdded(getDayMealItems(plan, selectedDay, selectedDateKey), recentlyAddedMealIds),
    [plan, selectedDay, selectedDateKey, recentlyAddedMealIds],
  );

  if (isPlanLoading || isYourFoodLoading) {
    return (
      <div className="space-y-3 px-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <IonSkeletonText key={i} animated className="h-21 w-full rounded-2xl" />
        ))}
      </div>
    );
  }

  const isAddingFood = pendingFoodAdd !== null;

  if (items.length === 0 && !isAddingFood) {
    return <MyPlanEmptyState onAddNext={onAddNext} />;
  }

  return (
    <div className="flex flex-1 flex-col overflow-y-hidden">
      <div className="relative flex-1 overflow-y-hidden">
        {isScrolled && <ListShadow />}
        <div
          onScroll={handleScroll}
          className={cn("no-scrollbar h-full overflow-y-auto px-4 pt-1 pb-40", "flex flex-col")}
          data-scroll-container
        >
          <h2
            className={cn(
              textVariants.bodySmall,
              "flex h-[40px] items-center !font-bold text-black uppercase",
            )}
            style={{ lineHeight: "30px" }}
          >
            Your Day
          </h2>

          <div className="flex flex-col gap-4">
            {isAddingFood && <AddingFoodCard />}
            {items.map(({ meal, mealTime, mealIndex }) => (
              <Fragment key={`${mealTime}-${mealIndex}-${meal.id ?? mealIndex}`}>
                <MealCard
                  meal={meal}
                  mealTime={mealTime}
                  mealDay={selectedDay}
                  targetDate={selectedDateKey}
                  mealIndex={mealIndex}
                  isMyFood={yourFoodMealsSet.has(meal.id ?? "")}
                  isTickedOff={meal.isTickedOff ?? false}
                  actionVariant="coach"
                />
              </Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
});
